import { Box, Stack } from "@mui/material";
import { useCallback, useReducer } from "react";
import { DashboardBackground } from "../../modules/dashboard/components/dashboard-background";
import { DashboardHeader } from "../../modules/dashboard/components/dashboard-header";
import { RecentProgramsCard } from "../../modules/dashboard/components/recent-programs-card";
import { RecentlyEditedSection } from "../../modules/dashboard/components/recently-edited-section";
import { FeedDragAndDrop } from "../../modules/dashboard/components/recently-edited/feed-drag-and-drop";
import { WhatsNewCard } from "../../modules/dashboard/components/whats-new-card";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { useFlags } from "../../server/optimizely";
import CreateContentPage from "../create-content/create-content-page";

import type { RecentlyEditedType } from "../../modules/dashboard/hooks/use-recently-edited";
import type { FC } from "react";

/**
 * The home page: the dashboard with the cha_4551_dashboard_2026 flag on, and the original home page,
 * for creating content, without it. Neither shows until the flag has loaded.
 */
const DashboardPage: FC = () => {
  const {
    flags: { cha_4551_dashboard_2026 },
    flagsLoaded,
  } = useFlags("cha_4551_dashboard_2026");
  // Bumped for each kind of thing archived or duplicated in the feed, so the header counts it again.
  const [refreshKeys, refreshCount] = useReducer(
    (keys: Record<RecentlyEditedType, number>, type: RecentlyEditedType) => ({
      ...keys,
      [type]: keys[type] + 1,
    }),
    { communication: 0, survey: 0, digital_asset: 0 },
  );
  // Bumped for each Communication copied onto the feed, so the feed shows it and the header counts it.
  const [copies, copied] = useReducer((count: number) => count + 1, 0);
  const onCopied = useCallback(() => {
    copied();
    refreshCount("communication");
  }, []);

  if (!flagsLoaded) return null;
  if (!cha_4551_dashboard_2026) return <CreateContentPage />;

  return (
    <Page
      title="Home | ChangeEngine"
      sx={{ position: "relative", isolation: "isolate", minHeight: "100vh" }}
    >
      <DashboardBackground />
      <PageContent maxWidth="lg" sx={{ mt: 4 }}>
        <Stack spacing={4}>
          <DashboardHeader refreshKeys={refreshKeys} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <RecentProgramsCard />
            <WhatsNewCard />
          </Box>
          <FeedDragAndDrop onCopied={onCopied}>
            <RecentlyEditedSection onChanged={refreshCount} refreshKey={copies} />
          </FeedDragAndDrop>
        </Stack>
      </PageContent>
    </Page>
  );
};

export default DashboardPage;
